import HeaderLogo from "../assets/icons/header-icon.svg?react";
import WalletIcon from "../assets/icons/coin-wallet.svg?react";
import AvatarIcon from "../assets/icons/avatar.svg?react";
import { useAuth } from "../context/AuthContext";
import { useWebSocket } from "../context/WebSocketContext";
import { useState, useEffect } from "react";
import { walletApi } from "../api/wallet";
// Import the popup components
import WithdrawPopup from "../components/Popup/WithdrawPopup";
import DepositPopupQR from "../components/Popup/DepositPopupQR";

import registerButton from "../assets/images/register-btn.png";
import loginButton from "../assets/images/login-btn.png";

import { useNavigate } from 'react-router-dom';

export default function Header() {
  const navigate = useNavigate();

  const { user, tokens } = useAuth();
  const { walletBalance, resetPaymentStatus, refreshWalletBalance } = useWebSocket();
  const [balance, setBalance] = useState(0);
  // Add state for popups
  const [showBalancePopup, setShowBalancePopup] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [showDepositQr, setShowDepositQr] = useState(false);

  // Format balance with commas and decimal places
  const formatBalance = (amount) => {
    if (!amount) return '0.00';
    return parseFloat(amount).toLocaleString('en-US', {
      style: 'currency',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      currency: 'PHP'
    });
  };

  // Fetch wallet balance on load
  useEffect(() => {
    const fetchWallet = async () => {
      if (user && tokens?.access?.token) {
        try {
          const wallet = await walletApi.getWallet(tokens.access.token, user.id);
          
          // Check if we got a redirected response (token expired)
          if (wallet && wallet.redirected) {
            console.log('Token expired during wallet fetch in header');
            return;
          }
          
          setBalance(wallet.balance);
        } catch (error) {
          console.error('Error fetching wallet:', error);
          // Don't rethrow to prevent UI issues
        }
      }
    };
    
    fetchWallet();
  }, [user, tokens]);

  // Use websocket balance if available, otherwise use the fetched balance
  const displayBalance = walletBalance || balance;

  // Handle QR deposit button click
  const handleQrDepositClick = () => {
    resetPaymentStatus(); // Reset payment status before showing QR deposit
    setShowBalancePopup(false); // Close the main popup
    setShowDepositQr(true);
  };

  // Handle withdraw button click
  const handleWithdrawClick = () => {
    setShowBalancePopup(false); // Close the main popup
    setShowWithdraw(true);
  };

  const isAuthenticated = user && tokens?.access?.token;

  return (
    <header className="relative flex items-center justify-between px-5 py-2">
      {/* Wallet Balance - Now a button */}
      {isAuthenticated ? (
        <button 
          className="relative flex items-center px-1 py-1 rounded-md z-10" 
          onClick={() => setShowBalancePopup(true)}
        style={{
          background: "linear-gradient(to right, #CE1F28, #FE0201)",
          borderColor: "#FFE500",
          borderWidth: "1px"
        }}>
        {/* White overlay gradient */}
        <div className="absolute inset-0 rounded-md" 
             style={{
               background: "linear-gradient(to bottom, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 100%)",
               pointerEvents: "none"
             }}>
        </div>
        
        <div className="flex-shrink-0 relative z-10">
          <WalletIcon className="w-6 h-6" />
        </div>
        <span className="font-semibold text-xs tracking-wide text-white relative z-10 pr-1 font-poppins">
          {formatBalance(displayBalance)}
        </span>
      </button>
      ) : (
        <button 
          className="relative flex items-center px-1 py-1 rounded-md z-10" 
          onClick={() =>
            // navigate to /register page
            navigate('/register')
          }
        >
          <img src={registerButton} alt="Register" />
        </button>
      )}

      {/* Logo - Absolutely positioned in center */}
      <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <HeaderLogo className="max-xs:w-[140px] max-xs:h-[49px]" />
      </div>


      {/* User Profile */}
      {isAuthenticated ? (
      <div className="flex items-center z-10">
        <span className="text-white font-poppins text-xs mr-1 max-w-[80px] truncate">
          {user?.name?.split(' ')[0] ?? 'Guest'}
        </span>
        <div className="w-8 h-8 rounded-full overflow-hidden bg-blue-600 flex items-center justify-center border-2 border-white">
          <span className="text-white font-poppins font-medium text-xs">
            {user?.name 
              ? user.name.split(' ').map(part => part[0]).join('').substring(0, 2).toUpperCase()
              : 'G'}
          </span>
        </div>
      </div>
      ) : (
        <button 
          className="relative flex items-center px-1 py-1 rounded-md z-10" 
          onClick={() =>
            navigate('/login')
          }
        >
          <img src={loginButton} alt="Login" />
        </button>
      )}

      {/* Balance Popup */}
      {showBalancePopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 w-80 max-w-[90%]">
            <h3 className="text-center text-lg font-semibold mb-3">Wallet Balance</h3>
            <p className="text-center text-2xl font-bold mb-4">
              {formatBalance(displayBalance)}
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button
                className="flex items-center justify-center gap-2 bg-white border border-[#0E64D1] rounded-full py-2 px-4 text-[#0E64D1] font-medium"
                onClick={handleQrDepositClick}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 4V20M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Deposit
              </button>
              <button
                className="flex items-center justify-center gap-2 bg-[#0E64D1] rounded-full py-2 px-4 text-white font-medium"
                onClick={handleWithdrawClick}
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M4 12H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Withdraw
              </button>
            </div>
            <button 
              className="mt-4 w-full text-gray-600 text-center"
              onClick={() => setShowBalancePopup(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Reuse the same popups from BalanceCard */}
      <DepositPopupQR
        isOpen={showDepositQr}
        onClose={() => setShowDepositQr(false)}
        currentBalance={formatBalance(displayBalance).replace("PHP", "").trim()}
      />

      <WithdrawPopup
        isOpen={showWithdraw}
        onClose={() => setShowWithdraw(false)}
        currentBalance={formatBalance(displayBalance).replace("PHP", "").trim()}
        defaultPaymentMethod={{
          type: "GCASH",
          accountNumber: "09187914980",
        }}
      />
    </header>
  );
}
